import React, { useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const YEARS = (() => {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = 2018; y <= current; y++) years.push(y);
  return years;
})();

const iso = (year, monthIdx, day) =>
  `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const CalendarMonth = ({ label, month, year, onMonthChange, selected, range, onPick }) => {
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ d, iso: iso(y, m, d), outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ d, iso: iso(year, month, d), outside: false });
  let next = 1;
  while (cells.length % 7 !== 0 || cells.length < 42) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ d: next, iso: iso(y, m, next), outside: true });
    next++;
    if (cells.length >= 42) break;
  }

  const prevMonth = () => (month === 0 ? onMonthChange(11, year - 1) : onMonthChange(month - 1, year));
  const nextMonth = () => (month === 11 ? onMonthChange(0, year + 1) : onMonthChange(month + 1, year));
  const inRange = (dIso) => range.from && range.to && dIso >= range.from && dIso <= range.to;
  const isSelected = (dIso) => dIso === selected;

  return (
    <div className="w-[280px] p-3">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="mb-3 flex items-center gap-2">
        <button
          onClick={prevMonth}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <select
          value={month}
          onChange={(e) => onMonthChange(Number(e.target.value), year)}
          className="h-9 flex-1 rounded-lg border border-gray-200 px-2 text-[14px] font-medium text-gray-900"
        >
          {MONTHS.map((m, idx) => (
            <option key={m} value={idx}>
              {m}
            </option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => onMonthChange(month, Number(e.target.value))}
          className="h-9 flex-1 rounded-lg border border-gray-200 px-2 text-[14px] font-medium text-gray-900"
        >
          {YEARS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          onClick={nextMonth}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900"
          aria-label="Next month"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7 text-center">
        {DOW.map((d) => (
          <span key={d} className="py-1 text-[12px] text-gray-500">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => (
          <button
            key={idx}
            onClick={() => onPick(cell.iso)}
            className={`flex h-9 items-center justify-center text-[14px] tabular-nums transition-colors ${
              cell.outside ? 'text-gray-300' : 'text-gray-900'
            } ${
              isSelected(cell.iso)
                ? 'rounded-lg bg-gray-900 font-medium !text-white'
                : inRange(cell.iso)
                  ? 'bg-gray-100'
                  : 'rounded-lg hover:bg-gray-100'
            }`}
          >
            {cell.d}
          </button>
        ))}
      </div>
    </div>
  );
};

// Dual From/To month calendar for picking a custom date range.
// `range` is { from, to } with yyyy-MM-dd strings (or null).
const CustomDateCalendar = ({ range, onChange, onComplete }) => {
  const fromDate = range.from ? new Date(`${range.from}T00:00:00`) : new Date();
  const toDate = range.to ? new Date(`${range.to}T00:00:00`) : new Date();
  const [fromView, setFromView] = useState({ m: fromDate.getMonth(), y: fromDate.getFullYear() });
  const [toView, setToView] = useState({ m: toDate.getMonth(), y: toDate.getFullYear() });

  const pickFrom = (dIso) => {
    if (range.to && dIso > range.to) {
      onChange({ from: dIso, to: null });
    } else {
      onChange({ from: dIso, to: range.to });
      if (range.to) onComplete?.();
    }
  };

  const pickTo = (dIso) => {
    if (range.from && dIso < range.from) {
      onChange({ from: dIso, to: range.from });
      onComplete?.();
    } else {
      onChange({ from: range.from, to: dIso });
      if (range.from) onComplete?.();
    }
  };

  const today = format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="select-none">
      <div className="flex flex-col divide-y divide-gray-200 sm:flex-row sm:divide-x sm:divide-y-0">
        <CalendarMonth
          label="From"
          month={fromView.m}
          year={fromView.y}
          onMonthChange={(m, y) => setFromView({ m, y })}
          selected={range.from}
          range={range}
          onPick={pickFrom}
        />
        <CalendarMonth
          label="To"
          month={toView.m}
          year={toView.y}
          onMonthChange={(m, y) => setToView({ m, y })}
          selected={range.to}
          range={range}
          onPick={pickTo}
        />
      </div>
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2.5">
        <button
          onClick={() => onChange({ from: null, to: null })}
          className="text-[13px] font-medium text-gray-900 hover:underline"
        >
          Clear
        </button>
        <button
          onClick={() => {
            const now = new Date();
            setFromView({ m: now.getMonth(), y: now.getFullYear() });
            setToView({ m: now.getMonth(), y: now.getFullYear() });
            onChange({ from: today, to: today });
            onComplete?.();
          }}
          className="text-[13px] font-medium text-gray-900 hover:underline"
        >
          Today
        </button>
      </div>
    </div>
  );
};

export default CustomDateCalendar;
